import React from 'react';
import theme from '@/style';
import currentBrand from '@/brands';

type WithdrawProgressiveThemeVar =
  | '--1'
  | '--withdraw-primary-gradient'
  | '--withdraw-coin-selected-background'
  | '--withdraw-amount-selected-text-color'
  | '--withdraw-submit-text-color';

type WithdrawProgressiveThemeStyle = React.CSSProperties &
  Record<WithdrawProgressiveThemeVar, string>;

const WITHDRAW_PROGRESSIVE_THEME_VARS: WithdrawProgressiveThemeVar[] = [
  '--1',
  '--withdraw-primary-gradient',
  '--withdraw-coin-selected-background',
  '--withdraw-amount-selected-text-color',
  '--withdraw-submit-text-color',
];

function colorToRgba(color: string, alpha: number) {
  const normalized = color.trim();
  if (!normalized.startsWith('#')) {
    return normalized;
  }
  const hex = normalized.slice(1);
  const fullHex =
    hex.length === 3
      ? hex
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : hex;
  if (fullHex.length !== 6) {
    return normalized;
  }
  const red = Number.parseInt(fullHex.slice(0, 2), 16);
  const green = Number.parseInt(fullHex.slice(2, 4), 16);
  const blue = Number.parseInt(fullHex.slice(4, 6), 16);
  if ([red, green, blue].some(value => Number.isNaN(value))) {
    return normalized;
  }
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export function useWithdrawProgressiveThemeStyle() {
  const brandBasicColor = currentBrand.theme.basicColor || {};
  const primaryColor = String(
    brandBasicColor.primary || theme.basicColor.primary,
  );
  const themeStyle = React.useMemo<WithdrawProgressiveThemeStyle>(
    () => ({
      '--1': primaryColor,
      '--withdraw-primary-gradient': 'linear-gradient(var(--1), var(--1))',
      '--withdraw-coin-selected-background': colorToRgba(primaryColor, 0.1),
      '--withdraw-amount-selected-text-color': '#fff',
      '--withdraw-submit-text-color': '#fff',
    }),
    [primaryColor],
  );

  React.useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const rootStyle = document.documentElement.style;
    const previousValues = WITHDRAW_PROGRESSIVE_THEME_VARS.map(
      name => [name, rootStyle.getPropertyValue(name)] as const,
    );

    WITHDRAW_PROGRESSIVE_THEME_VARS.forEach(name => {
      rootStyle.setProperty(name, themeStyle[name]);
    });

    return () => {
      previousValues.forEach(([name, value]) => {
        if (value) {
          rootStyle.setProperty(name, value);
        } else {
          rootStyle.removeProperty(name);
        }
      });
    };
  }, [themeStyle]);

  return themeStyle;
}

const WITHDRAW_PROGRESSIVE_VIEWPORT_HEIGHT_VAR =
  '--withdraw-progressive-viewport-height';

export function useWithdrawProgressiveViewportHeightStyle() {
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const updateViewportHeight = () => {
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty(
        WITHDRAW_PROGRESSIVE_VIEWPORT_HEIGHT_VAR,
        `${height}px`,
      );
    };

    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('scroll', updateViewportHeight);

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.visualViewport?.removeEventListener(
        'resize',
        updateViewportHeight,
      );
      window.visualViewport?.removeEventListener(
        'scroll',
        updateViewportHeight,
      );
      document.documentElement.style.removeProperty(
        WITHDRAW_PROGRESSIVE_VIEWPORT_HEIGHT_VAR,
      );
    };
  }, []);

  return React.useMemo(
    () => ({
      height: `var(${WITHDRAW_PROGRESSIVE_VIEWPORT_HEIGHT_VAR}, 100vh)`,
    }),
    [],
  );
}
